import {
    Component, Inject, OnInit, ViewChild
} from '@angular/core';
import {
    AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CrudService } from 'src/app/services/crud.service';
import { LocalStorageService } from 'src/app/services/localStorage.service';
import { environment } from 'src/environments/environment';
import { DeleteConfirmationComponent } from 'src/app/layout/modals/delete-confirmation/delete-confirmation.component';

// Custom validator to prevent whitespace-only input
export function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.trim().length === 0) {
        return { whitespace: true };
    }
    return null;
}

@Component({
    selector: 'app-create-affiliate-code',
    templateUrl: './create-affiliate-code.component.html',
    styleUrls: ['./create-affiliate-code.component.css']
})
export class CreateAffiliateCodeComponent implements OnInit {
    @ViewChild('deleteDialogRef') deleteDialogRef: DeleteConfirmationComponent;

    affiliateCodeForm!: FormGroup;
    affiliateCodeData: any;
    url: string = '';
    currentUserId: any;
    userInfo: any;

    spinner = false;
    processing = false;
    isFormSubmitted = false;
    isEditMode = false;
    today: string;
    originalStartDate!: Date;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private toaster: ToastrService,
        private crudService: CrudService,
        private localStorageService: LocalStorageService,
        private dialogRef: MatDialogRef<CreateAffiliateCodeComponent>,
        private dialog: MatDialog
    ) {
        this.currentUserId = this.localStorageService.getItem('user');
        this.today = new Date().toISOString().split('T')[0];
    }

    ngOnInit(): void {
        this.initializeForm();
        this.getUserInfo();

        if (this.data?.codeData) {
            this.isEditMode = true;
            this.affiliateCodeData = this.data.codeData;
            this.originalStartDate = new Date(this.affiliateCodeData.startDate);
            this.patchForm();
        }
    }

    initializeForm(): void {
        this.affiliateCodeForm = this.fb.group({
            code: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(25), noWhitespaceValidator]],
            name: ['', [Validators.required, Validators.maxLength(50), noWhitespaceValidator]],
            description: ['', [Validators.required, Validators.maxLength(200), noWhitespaceValidator]],
            startDate: ['', [Validators.required, this.startDateValidator()]],
            endDate: ['']
        });

        this.affiliateCodeForm.get('startDate')?.valueChanges.subscribe(() => {
            this.affiliateCodeForm.get('endDate')?.updateValueAndValidity();
        });

        this.affiliateCodeForm.get('endDate')?.setValidators(this.endDateValidator());
    }

    patchForm(): void {
        const { code, name, description, startDate, endDate } = this.affiliateCodeData;
        this.affiliateCodeForm.patchValue({
            code,
            name,
            description,
            startDate: this.formatDate(startDate),
            endDate: endDate ? this.formatDate(endDate) : ''
        });

        this.updateURL(code);
    }

    formatDate(date: string): string {
        return new Date(date).toISOString().split('T')[0];
    }

    getUserInfo(): void {
        this.spinner = true;
        this.crudService.getUserInfoV2(this.currentUserId).subscribe((res) => {
            this.userInfo = res.data?.userInfo;
            this.updateURL(this.affiliateCodeForm.get('code')?.value || '');
            this.spinner = false;
        });
    }

    updateURL(code: string): void {
        const base = `${environment.currentDomain}my-community/${this.userInfo?.business?.communityUrl}`;
        this.url = code ? `${base}?affiliateCode=${code}` : base;
    }

    startDateValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const selectedDate = new Date(control.value);
            const today = new Date();
            selectedDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            if (this.isEditMode && this.originalStartDate) {
                const original = new Date(this.originalStartDate);
                original.setHours(0, 0, 0, 0);
                if (selectedDate.getTime() === original.getTime()) return null;
            }

            return selectedDate < today ? { pastDate: true } : null;
        };
    }

    endDateValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const start = new Date(this.affiliateCodeForm.get('startDate')?.value);
            const end = new Date(control.value);
            if (!control.value || isNaN(end.getTime()) || isNaN(start.getTime())) return null;
            return end <= start ? { invalidEndDate: true } : null;
        };
    }

    errorHandling(control: string, error: string): boolean {
        const ctrl = this.affiliateCodeForm.get(control);
        return ctrl?.hasError(error) && (ctrl?.dirty || ctrl?.touched || this.isFormSubmitted);
    }

    onSubmit(): void {
        this.isFormSubmitted = true;

        ['code', 'name', 'description'].forEach(field => {
            const control = this.affiliateCodeForm.get(field);
            if (control && typeof control.value === 'string') {
                control.setValue(control.value.trim(), { emitEvent: false });
            }
        });

        if (this.affiliateCodeForm.invalid) return;

        this.processing = true;
        const formValue = this.affiliateCodeForm.value;

        if (this.isEditMode) {
            const payload = { ...formValue, id: this.affiliateCodeData._id };
            this.crudService.updateAffiliateCode(payload).subscribe(() => {
                this.toaster.success('Affiliate code updated successfully');
                this.dialogRef.close(true);
            }).add(() => this.processing = false);
        } else {
            this.crudService.createAffiliateCode(formValue).subscribe(() => {
                this.toaster.success('Affiliate code created successfully');
                this.dialogRef.close(true);
            }).add(() => this.processing = false);
        }
    }

    generateCode(): void {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let code = '';
        for (let i = 0; i < 25; i++) {
            code += characters[Math.floor(Math.random() * characters.length)];
        }

        this.affiliateCodeForm.get('code')?.setValue(code);
        this.updateURL(code);
    }

    onChangeCode(event: Event): void {
        const input = (event.target as HTMLInputElement).value;
        this.updateURL(input);
    }

    onCopySuccess(event: any): void {
        event.isSuccess
            ? this.toaster.info('Link copied successfully !!')
            : this.toaster.error('Failed to copy link.');
    }

    openConfirmationDeleteAffiliateCode(item: any): void {
        this.deleteDialogRef.open({
            header: 'Delete Affiliate Code',
            title: 'Are you sure you want to delete this affiliate code?'
        }, (confirmed) => {
            if (confirmed) {
                this.processing = true;
                this.crudService.deleteAffiliateCode(item._id).subscribe(() => {
                    this.toaster.success('Affiliate code deleted successfully !!');
                    this.dialogRef.close(true);
                }).add(() => this.processing = false);
            }
        });
    }

    copyUrlCode() {
        this.toaster.info('URL copied successfully !!');
    }
}
